import type { ValueTypeEntry } from "../index";
import { DateEditor, type DateValue } from "./date-editor";

/**
 * UTC-anchored date format. Mirrors `formatDate` in
 * `packages/ui/src/data-display/field-value-renderer.tsx` (~lines 46–70):
 * builds a Date, formats date parts in UTC, and appends time only when
 * the source string carries an explicit time component that is not
 * midnight UTC. On unparseable input, returns the input verbatim.
 */
// Module-cached formatters. Constructing `Intl.DateTimeFormat` is expensive
// relative to `.format()`, and per-call `toLocaleDateString` /
// `toLocaleTimeString` allocate a fresh formatter every invocation. Caching
// at module scope keeps the output byte-identical while removing the
// per-render allocation.
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "UTC",
});

function formatDateValue(value: DateValue): string {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value);
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return str;

  const hasTime =
    (str.includes("T") || str.includes(" ")) &&
    (date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0 || date.getUTCSeconds() !== 0);

  const dateStr = DATE_FMT.format(date);

  if (hasTime) {
    const timeStr = TIME_FMT.format(date);
    return `${dateStr} ${timeStr}`;
  }

  return dateStr;
}

/**
 * Built-in `date` field type. Values are ISO-8601 strings in and out.
 */
export const dateType: ValueTypeEntry<DateValue> = {
  name: "date",
  format(value) {
    return formatDateValue(value);
  },
  render({ value }) {
    return formatDateValue(value);
  },
  display({ value }) {
    const narrowed = typeof value === "string" ? value : null;
    return formatDateValue(narrowed);
  },
  edit({ value, onCommit, onCancel }) {
    return <DateEditor value={value} onCommit={onCommit} onCancel={onCancel} />;
  },
};
